import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPrerenderBlogPost,
  getPrerenderBlogPostList,
  getPrerenderListing,
  getPrerenderListingList,
  getPrerenderWarp,
  getPrerenderWarpList,
  isReactSnapCrawler,
} from "../utils/prerenderData";
import { isInteractiveClient } from "../utils/clientRenderMode";
import {
  deleteRouteCache,
  fetchRouteCache,
  getRouteCache,
} from "../utils/routeDataCache";

const DEFAULT_SNAPSHOT_AFTER_MS = 300;

/**
 * Cache-first route data hook for single-render page loads.
 *
 * - Interactive clients: wait for fresh data; show build-time snapshot after
 *   showSnapshotAfterMs if fetch is still pending (hybrid stale-while-revalidate).
 * - ReactSnap crawlers: use prerender fallbacks for SEO.
 * - Filter changes after mount refetch silently (no entry spinner).
 */
export default function useRouteResource({
  cacheKey,
  fetch,
  enabled = true,
  prerenderFallback = null,
  showSnapshotAfterMs = DEFAULT_SNAPSHOT_AFTER_MS,
}) {
  const isCrawler = isReactSnapCrawler();
  const interactive = isInteractiveClient();

  const readInitial = () => {
    if (isCrawler && prerenderFallback != null) {
      return prerenderFallback;
    }
    if (interactive && cacheKey) {
      const cached = getRouteCache(cacheKey);
      if (cached !== undefined) {
        return cached;
      }
    }
    return null;
  };

  const [data, setData] = useState(readInitial);
  const [error, setError] = useState(null);
  const [isSnapshot, setIsSnapshot] = useState(false);
  const [isReady, setIsReady] = useState(() => {
    if (isCrawler && prerenderFallback != null) return true;
    if (interactive && cacheKey && getRouteCache(cacheKey) !== undefined) {
      return true;
    }
    return false;
  });

  const hadInitialData = useRef(isReady);
  const fetchRef = useRef(fetch);
  const prerenderFallbackRef = useRef(prerenderFallback);
  fetchRef.current = fetch;
  prerenderFallbackRef.current = prerenderFallback;

  const load = useCallback(
    async (silent = false, force = false) => {
      if (!enabled) {
        return;
      }

      try {
        if (force && cacheKey) {
          deleteRouteCache(cacheKey);
        }

        const result = cacheKey
          ? await fetchRouteCache(cacheKey, () => fetchRef.current())
          : await fetchRef.current();

        setData(result);
        setError(null);
        setIsReady(true);
        setIsSnapshot(false);
      } catch (err) {
        setError(err.message || "Failed to load data");
        if (!silent || !hadInitialData.current) {
          setIsReady(true);
        }
      }
    },
    [cacheKey, enabled]
  );

  useEffect(() => {
    const snapshotFallback = prerenderFallbackRef.current;

    if (isCrawler) {
      if (snapshotFallback != null) {
        setData(snapshotFallback);
        setIsReady(true);
      }
      return undefined;
    }

    if (!enabled) {
      return undefined;
    }

    const cached = cacheKey ? getRouteCache(cacheKey) : undefined;
    if (cached !== undefined) {
      setData(cached);
      setError(null);
      setIsReady(true);
      setIsSnapshot(false);
      hadInitialData.current = true;

      let cancelled = false;
      (async () => {
        try {
          deleteRouteCache(cacheKey);
          const result = await fetchRouteCache(cacheKey, () => fetchRef.current());
          if (!cancelled) {
            setData(result);
            setError(null);
            setIsSnapshot(false);
          }
        } catch (err) {
          if (!cancelled) {
            setError(err.message || "Failed to load data");
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    // Cache miss: keep stale data visible during filter refetch (no entry spinner).
    if (hadInitialData.current) {
      let cancelled = false;
      (async () => {
        try {
          const result = cacheKey
            ? await fetchRouteCache(cacheKey, () => fetchRef.current())
            : await fetchRef.current();
          if (!cancelled) {
            setData(result);
            setError(null);
            setIsSnapshot(false);
          }
        } catch (err) {
          if (!cancelled) {
            setError(err.message || "Failed to load data");
          }
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    let cancelled = false;
    let resolved = false;
    let snapshotTimerId = null;

    const showSnapshotIfSlow = () => {
      const fallback = prerenderFallbackRef.current;
      if (
        resolved ||
        cancelled ||
        fallback == null ||
        showSnapshotAfterMs < 0
      ) {
        return;
      }
      setData(fallback);
      setIsReady(true);
      setIsSnapshot(true);
    };

    if (snapshotFallback != null && showSnapshotAfterMs >= 0) {
      snapshotTimerId = setTimeout(showSnapshotIfSlow, showSnapshotAfterMs);
    }

    (async () => {
      try {
        const result = cacheKey
          ? await fetchRouteCache(cacheKey, () => fetchRef.current())
          : await fetchRef.current();
        if (!cancelled) {
          resolved = true;
          if (snapshotTimerId != null) {
            clearTimeout(snapshotTimerId);
          }
          setData(result);
          setError(null);
          setIsReady(true);
          setIsSnapshot(false);
          hadInitialData.current = true;
        }
      } catch (err) {
        if (!cancelled) {
          resolved = true;
          if (snapshotTimerId != null) {
            clearTimeout(snapshotTimerId);
          }
          setError(err.message || "Failed to load data");
          setIsReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
      resolved = true;
      if (snapshotTimerId != null) {
        clearTimeout(snapshotTimerId);
      }
    };
  }, [cacheKey, enabled, isCrawler, showSnapshotAfterMs]);

  const refetch = useCallback(() => load(true, true), [load]);

  return { data, error, isReady, isSnapshot, refetch, setData };
}

export function usePrerenderListings() {
  return getPrerenderListingList();
}

export function usePrerenderWarps() {
  return getPrerenderWarpList();
}

export function usePrerenderBlogPosts() {
  return getPrerenderBlogPostList();
}

export function usePrerenderListing(id) {
  return getPrerenderListing(id);
}

export function usePrerenderWarp(id) {
  return getPrerenderWarp(id);
}

export function usePrerenderBlogPost(slug) {
  return getPrerenderBlogPost(slug);
}
